import type { MediaSelection, TimeRange } from '@mimir/common/util/timeRange';
import type { ArtifactReference, AudioTsSegmentRange, TsSegmentRange } from '@mimir/primitive/artifactReference';
import { isProxyOnlyArtifactReference } from '@mimir/primitive/artifactReference';
import type { DeliveryStrategy, DownloadBlocker, DownloadIdentity, DownloadPlan, DownloadProspect } from './downloadIdentity';
import type { ClientItem } from '@mimir/common/dto/searchResultHit';
import type { ItemArtefact } from '@mimir/common/dto/transferRequestDtos';
import { SystemDefaultField } from '@mimir/common/mdf/mdfDefaults';
import { getItemSystemDefaultField } from '@mimir/common/mdf/MetaDataForm';
import type { ItemMetaData } from '@mimir/common/dto/fieldValue';
import type { MediaAvailability } from '@mimir/common/dto/mediaAvailability';
import { StreamRoleId } from '@mimir/common/dto/mediaAvailability';
import { urlResolvableItemKeys } from '../storageManager/resolveTransferUrl';
import type { VirtualClipModel } from '@mimir/common/dto/virtualClipModel';
import { isInstanceOfVirtualClip } from '@mimir/common/dto/virtualClipUtil';
import { addFuzzy, divideFuzzy, fractionToFuzzy, integerToFuzzy, multiplyFuzzy, subtractFuzzy } from '@mimir/common/util/fuzzyMath';
import { getExtname } from '@mimir/common/platformAbstraction/pathComposition';
import type { BackendClient } from '@mimir/common/util/backendClient';
import { api_getItemFields } from '../util/api';
import { isCurrentlyLive } from '@mimir/common/item/media';
import type { LocalSegmentedMediaState } from '@mimir/common/util/mediaState';
import { deriveArtifactReferenceFromItem, isFlatHighresTsItem } from './resolveArtifactReference';

const downloadableItemKeys = <const>['id', 'media', 'aaf', 'itemType', 'virtualClipInfo', 'mediaDuration', 'originalFileName', 'indexUrl', 'isLive', 'hasMediaIngestIntegrationExternalHighres'];
export type DownloadableItem = Pick<ClientItem, typeof downloadableItemKeys[number] | typeof urlResolvableItemKeys[number]>;

/**
 * Context and input that affect which strategies we use for obtaining an
 * artifact.
 */
export interface DownloadRequestBase {
  isProxyWorkflowEnabled: boolean;
  isPremiereStandaloneMode: boolean;
}

export interface UnresolvedDownloadRequest extends DownloadRequestBase {
  item: DownloadableItem;
  requestedArtefact: ItemArtefact;

  /**
   * Approximate time range, relative to the virtual clip start.
   *
   * Note that unlike sourceMediaRange, this this timeRange needs to be
   * reinterpreted using the VirtualClipModel of an item, if any, before trying
   * to use the time range boundaries as media positions in the underlying
   * source media.
   */
  timeRange: TimeRange | null;

  /**
   * Potentially accurate time range, within the source media.
   */
  sourceMediaRange: MediaSelection;

  /**
   * For proxy only mode we should try to fetch as little as possible when
   * downloading the proxy even if this means that working in highres mode
   * in the future will not be possible for the clip.
   */
  proxyOnlyOptimizations: boolean;

  /**
   * If high-res sub clipping is enabled then any item with an associated
   * index file will imported as a sub section of the original media. This
   * means that the high res for these clips will be downloaded through the
   * S3 gateway using partial high res download (MXF subclipping etc.)
   *
   * Note that this will disable the direct use of high res is mounted folders
   */
  partialHighresDownload: boolean;

  /**
   * If set, then any TS segments being downloaded will be remuxed to mp4
   */
  remuxTsToMp4: boolean;

  wantGrowingArtifact: boolean;

  subclipExpansionRoom: number;
}

/**
 * Specialization for when the requester knows exactly what they want to
 * download.
 */
export interface IdentifiedDownloadRequest extends DownloadRequestBase {
  downloadIdentity: DownloadIdentity
}

export type DownloadRequest = IdentifiedDownloadRequest | UnresolvedDownloadRequest;

export function isIdentifiedDownloadRequest(req: DownloadRequest): req is IdentifiedDownloadRequest {
  const partial: Partial<IdentifiedDownloadRequest> = req;
  return partial.downloadIdentity != null;
}

export function isUnresolvedDownloadRequest(req: DownloadRequest): req is UnresolvedDownloadRequest {
  const partial: Partial<UnresolvedDownloadRequest> = req;
  return partial.item != null;
}

/**
 * Refreshes the item before planning download to make sure item states like the 'media' object
 * is up to date.
 */
export async function refreshItemAndPlanDownload(
  req: DownloadRequest,
  backendClient: BackendClient,
): Promise<DownloadProspect> {
  if (isIdentifiedDownloadRequest(req)) {
    return planDownload(req);
  }

  const freshItem: DownloadableItem = await api_getItemFields(
    backendClient,
    req.item.id,
    [...downloadableItemKeys, ...urlResolvableItemKeys]
  );
  return planDownload({ ...req, item: freshItem });
}

/**
 * Decide which strategies to use for obtaining an artifact.
 */
export function planDownload(req: DownloadRequest): DownloadProspect {
  let dlId: DownloadIdentity;
  let delivery: DeliveryStrategy;

  if (isIdentifiedDownloadRequest(req)) {
    dlId = req.downloadIdentity;
    delivery = { type: 'DeliverFile' };
  } else {
    const resolved = resolveDownloadPlan(req);
    if (!isValidPlan(resolved)) {
      return resolved;
    }
    dlId = resolved.dlId;
    delivery = resolved.deliver;
  }

  const validationResult = validateDownloadPlan(dlId, req);
  if (validationResult) {
    return validationResult;
  }

  return {
    blocker: null,
    delegate: implicitDelegationStrategy(req.isPremiereStandaloneMode),
    dlId,
    deliver: delivery
  };
}


function validateDownloadPlan(
  dlId: DownloadIdentity,
  req: DownloadRequest
): DownloadBlocker | null {
  if (dlId.artefact === 'highres' && isProxyOnlyArtifactReference(dlId.artifactReference)) {
    return { blocker: "Can't download highres for clip because it was imported with proxy-only optimizations enabled" };
  }

  const refType = dlId.artifactReference.type;

  if (refType === 'FlatFileReference' && !req.isPremiereStandaloneMode) {
    if (isUnresolvedDownloadRequest(req) && req.item.media) {
      const codecsByStreamId = req.item.media.codecsByStreamId;
      if (dlId.artefact === 'highres') {
        const hasHighResVideoTsSegments = Boolean(codecsByStreamId[StreamRoleId.LiveRecordHighresTs]);
        if (hasHighResVideoTsSegments) {
          return { blocker: 'The external storage manager does not support downloading the selected media time range.' };
        }
      }
      if (dlId.artefact === 'proxy') {
        const hasFlatProxy = Boolean(req.item.editProxyUrl) || Boolean(req.item.proxy) || req.item.hasEditProxy;
        if (hasFlatProxy) {
          return null;
        }

        const hasProxyTsSegments = Boolean(codecsByStreamId[StreamRoleId.LiveRecordAudioOnlyHighresTs])
          || Boolean(codecsByStreamId[StreamRoleId.LiveRecordWebproxyTs]);
        if (hasProxyTsSegments) {
          return { blocker: 'The external storage manager does not support downloading the selected media time range.' };
        }
      }
    }
  }

  return null;
}

function isValidPlan(prospect: DownloadProspect): prospect is DownloadPlan {
  return prospect.blocker === null;
}

function resolveDownloadPlan(req: UnresolvedDownloadRequest): DownloadProspect {
  const media = req.item.media;
  const artefact = req.requestedArtefact;
  const wantHighresOrEditProxy = artefact == 'highres' || artefact == 'proxy';

  if (wantHighresOrEditProxy && media != null) {
    const codecsByStreamId = media.codecsByStreamId;
    if (codecsByStreamId != null) {
      /*
        TODO: Introduce edit proxy stream role, and stop conflating it with web
        proxy stream role.
      */
      const derivedArtifactReference = deriveArtifactReferenceFromItem(req.item);
      if (derivedArtifactReference.type === 'AudioTsSegmentRange') {
        return makeTsSegmentDownloadPlan(req, media, artefact, false, true);
      }
      // NOTE: For cloud live both proxy and highres is segmented TS.
      // TsSegmentRange highres can be either segmented or flat file.
      if (derivedArtifactReference.type === 'TsSegmentRange') {
        return makeTsSegmentDownloadPlan(req, media, artefact, isFlatHighresTsItem(req.item), false);
      }
      // For old Kelda Live folder ingest we have a flat file highres,
      // but the proxy is segmented TS.
      if (derivedArtifactReference.type === 'TruncatedMp4') {
        return makeTruncatedMp4Plan(req, artefact, derivedArtifactReference);
      }
    }
  }
  // Flat file. Highres and proxy are both flat files.
  return makeFlatFilePlan(req, artefact);
}

/*
* Making Download Plans
*/

function makeTsSegmentDownloadPlan(
  req: UnresolvedDownloadRequest,
  media: MediaAvailability,
  artefact: ItemArtefact,
  flatFileHighRes: boolean,
  audioTs: boolean,
): DownloadProspect {
  if (req.remuxTsToMp4 && req.wantGrowingArtifact) {
    return { blocker: 'Cannot download growing TS artifact when remux to MP4 is enabled' };
  }

  let first: number;
  let last: number;
  const sourceMediaRange = req.sourceMediaRange;
  const segmentDuration = fractionToFuzzy(media.segmentDuration);
  const bounds = sourceMediaRange.bounds;
  const whole = sourceMediaRange.whole;

  if (whole) {
    first = 0;
    last = media.segmentCount - 1;
  } else {
    const subclipExpansionRoom = { approximate: req.subclipExpansionRoom };
    const startForSubclip = subtractFuzzy(bounds.start, subclipExpansionRoom);
    const endForSubclip = addFuzzy(bounds.end, subclipExpansionRoom);
    const segStart = divideFuzzy(startForSubclip, segmentDuration);
    const segEnd = divideFuzzy(endForSubclip, segmentDuration);
    first = Math.max(Math.floor(segStart.approximate), 0);
    last = Math.min(Math.floor(segEnd.approximate), (media.segmentCount - 1));
  }

  let highResExt : string | undefined;

  // If we are importing the whole media there is no need for us to go through
  // the complexity of doing MXF subclipping so we just disable the index in
  // that case
  let useIndex : boolean = false;

  if (flatFileHighRes) {
    if (!req.item.originalFileName) {
      return { blocker: 'Expected item with flat file highres to have originalFileName' };
    }

    // We know that original filename will not contain any directories
    // so we can use any path separator here.
    const pathSep = '/';
    highResExt = getExtname(req.item.originalFileName, pathSep);

    // Enable the use of index file for partial highres download if the item has an
    // index file and the user has requested this kind of import.
    if (!whole && (req.item.indexUrl !== undefined) && req.partialHighresDownload) {
      useIndex = true;
    }

    // The user may also choose to make a proxy-only subclip when in proxy mode
    const proxyOnlyImport = (artefact === 'proxy') && req.proxyOnlyOptimizations;

    // If neither partial highres nor proxy only import is enabled then we must adjust the
    // first segment time to zero so that the full highres media can be linked with the
    // premiere subclip
    if (!useIndex && !proxyOnlyImport) {
      first = 0;
    }
  }

  const skipped = multiplyFuzzy(integerToFuzzy(first), segmentDuration);
  // Virtual clips have no highres of their own; their media comes from the master clip.
  const vci = isInstanceOfVirtualClip(req.item.virtualClipInfo) ? req.item.virtualClipInfo : null;
  const itemId = vci !== null ? vci.masterClipId : req.item.id;

  const artifactReference: TsSegmentRange | AudioTsSegmentRange = audioTs
    ? {
      type: 'AudioTsSegmentRange',
      itemId,
      segmentTimes: {
        first,
        last
      },
      remuxTo: req.remuxTsToMp4 ? 'fmp4' : undefined,
    }
    : {
      type: 'TsSegmentRange',
      itemId,
      segmentTimes: {
        first,
        last
      },
      highResExt,
      useIndex,
      remuxTo: (req.remuxTsToMp4 ? 'fmp4' : undefined),
      isGrowing:
        isCurrentlyLive(req.item) &&
        req.wantGrowingArtifact,
    };

  const delegate = implicitDelegationStrategy(req.isPremiereStandaloneMode);

  return {
    blocker: null,
    delegate,
    dlId: {
      itemId,
      artefact,
      artifactReference,
    },
    deliver: {
      type: 'DeliverSubclip',
      whole,
      artifactRange: {
        start: subtractFuzzy(bounds.start, skipped),
        end: subtractFuzzy(bounds.end, skipped),
      },
      startTimeCode: bounds.start.approximate,
    }
  };
}

function makeTruncatedMp4Plan(
  req: UnresolvedDownloadRequest,
  artefact: ItemArtefact,
  artifactReference: ArtifactReference,
): DownloadPlan {
  const deliver: DeliveryStrategy = !req.sourceMediaRange.whole
    ? getDeliveryStrategyForTimeRaged(req)
    : { type: 'DeliverFile' };

  return {
    blocker: null,
    delegate: implicitDelegationStrategy(req.isPremiereStandaloneMode),
    dlId: {
      itemId: req.item.id,
      artefact,
      artifactReference,
    },
    deliver,
  };
}

function makeFlatFilePlan(req: UnresolvedDownloadRequest, artefact: ItemArtefact): DownloadPlan {
  const deliver: DeliveryStrategy = !req.sourceMediaRange.whole
    ? getDeliveryStrategyForTimeRaged(req)
    : { type: 'DeliverFile' };

  return {
    blocker: null,
    delegate: implicitDelegationStrategy(req.isPremiereStandaloneMode),
    dlId: {
      itemId: req.item.id,
      artefact,
      artifactReference: { type: 'FlatFileReference', itemId: req.item.id },
    },
    deliver,
  };
}

function implicitDelegationStrategy(isPremiereStandaloneMode: boolean) {
  return isPremiereStandaloneMode ? 'Direct' : 'TransferRequest';
}

export function makeTitle(plan: DownloadPlan, item: { metadata?: Partial<ItemMetaData> }, assignedTitle?: string) {
  // If a custom title is assigned to a file through import dialog, it will be used
  if (assignedTitle) {
    return assignedTitle;
  }

  const baseTitle = getItemSystemDefaultField(item, SystemDefaultField.Title)
    ?? item.metadata?.formData?.title // Special handling when 'fieldId' is used instead of 'id' in metadata (e.g. compact-view).
    ?? 'item';
  return baseTitle + getTitleSuffix(plan.dlId.artifactReference);
}

const subscripts: Record<string, string> = {
  '0': '₀',
  '1': '₁',
  '2': '₂',
  '3': '₃',
  '4': '₄',
  '5': '₅',
  '6': '₆',
  '7': '₇',
  '8': '₈',
  '9': '₉',
  '-': '₋',
};

export function getTitleSuffix(artifactReference: ArtifactReference) {
  switch (artifactReference.type) {
    case 'AudioTsSegmentRange': {
      const range = artifactReference.segmentTimes.first + '-' + artifactReference.segmentTimes.last;
      return ' ' + [...range].map(c => subscripts[c]).join('');
    }
    case 'TsSegmentRange': {
      const range = artifactReference.segmentTimes.first + '-' + (artifactReference.isGrowing ? '' : artifactReference.segmentTimes.last);
      return ' ' + [...range].map(c => subscripts[c]).join('');
    }
  }

  return '';
}

/**
 * Return the media source range of the whole virtual clip.
 */
export function getMediaSourceRangeOfWholeVirtualClip(virtualClipInfo: VirtualClipModel): MediaSelection {
  return {
    whole: false,
    bounds: {
      start: { approximate: virtualClipInfo.inPoint / 1000 },
      end: { approximate: virtualClipInfo.outPoint / 1000 }
    }
  };
}

function getDeliveryStrategyForTimeRaged(req: UnresolvedDownloadRequest): DeliveryStrategy {
  const vci = req.item.virtualClipInfo;
  const isVirtual = isInstanceOfVirtualClip(vci);
  const startTimeCode = isVirtual ? vci.inPoint / 1000 : req.sourceMediaRange.bounds.start.approximate;
  return {
    type: 'DeliverSubclip',
    whole: false,
    artifactRange: isVirtual ? {
      start: { approximate: vci.inPoint / 1000 },
      end: { approximate: vci.outPoint / 1000 },
    } : req.sourceMediaRange.bounds,
    startTimeCode,
  };
}

/**
 * For TruncatedMp4, adjusts lastSegmentIndex to match the local segmented media
 * state (e.g. a subclip end point). For all other artifact types, returns the
 * reference unchanged.
 */
export function refineArtifactReferenceForResume(
  ref: ArtifactReference,
  segmentedMediaState: LocalSegmentedMediaState | undefined,
): ArtifactReference {
  if (ref.type !== 'TruncatedMp4' || segmentedMediaState === undefined) {
    return ref;
  }
  return { ...ref, lastSegmentIndex: segmentedMediaState.lastSegmentIndex ?? ref.lastSegmentIndex };
}
